/**
 * First-URL extraction for the link check.
 *
 * ⚠️ THIS MUST STAY BYTE-IDENTICAL TO `extractFirstUrl` in
 * `supabase/functions/_shared/utm-stamper.ts` (:153-164). The stamper stamps
 * the FIRST http(s) URL it finds in a caption; the link check's entire job is
 * to predict whether that stamping will produce a clickable, attributable
 * link. Two different regexes here would be the grader and the stamper
 * disagreeing about whether a caption even has a link — a new silent no-op
 * inside the function built to prevent silent no-ops.
 *
 * The orchestrator patch in this lane's report makes utm-stamper.ts re-export
 * THIS module. Until that lands the two copies must be kept identical by hand.
 */

// URL char class excludes whitespace and the bracket/quote characters that
// commonly wrap a URL in prose, so "(see https://x.com)" does not capture the
// trailing ")". Legitimate parens inside a URL are a documented residual risk
// accepted at current volumes.
const URL_RE = /https?:\/\/[^\s<>()[\]{}"'`]+/i;
// Sentence punctuation a URL almost never ends with; trimmed off the tail.
const TRAILING_PUNCT_RE = /[.,;:!?]+$/;

/** First http(s) URL substring in `text`, or null. Pure. */
export function extractFirstCaptionUrl(text: string): string | null {
  if (typeof text !== 'string' || text.length === 0) return null;
  const match = text.match(URL_RE);
  if (!match) return null;
  const url = match[0].replace(TRAILING_PUNCT_RE, '');
  return url.length > 0 ? url : null;
}
